import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { Bell, CheckCheck, Circle, Inbox, X } from "lucide-react";
import {
  notificationsApi,
  relativeTime,
  CATEGORIES,
  PRIORITIES,
} from "@/lib/notifications";

/**
 * NotificationBell — global bell rendered once from `<Layout>`.
 * Wave 2A.2 uses lightweight polling every 60s + on-open refresh.
 * No websockets, no toasts, no external delivery.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [tab, setTab] = useState("unread");
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await notificationsApi.list(tab);
      setItems(r?.items || []);
      setUnread(r?.unread_count ?? 0);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  // Background poll — cheap because in-memory, no Mongo.
  // NOTE: Do NOT use a didInitRef guard here — the cleanup on StrictMode's
  // first-then-second effect invocation would kill the polling interval,
  // leaving unread count stale. Accepting one extra initial poll in dev
  // is preferable to a broken production interval.
  useEffect(() => {
    let mounted = true;
    const poll = async () => {
      if (!mounted) return;
      try {
        const r = await notificationsApi.list("unread");
        if (mounted) setUnread(r?.unread_count ?? 0);
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(poll, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      // Narrow viewports (< 480px) — anchor to a small right/left inset
      // and let the Center stretch to available width. Wider viewports
      // right-align to the bell.
      if (vw < 480) {
        setAnchor({ top: r.bottom + 6, right: 8, left: 8, narrow: true });
      } else {
        setAnchor({ top: r.bottom + 6, right: Math.max(8, vw - r.right), narrow: false });
      }
    }
    setOpen(true);
  };

  const badgeText = unread > 99 ? "99+" : String(unread);

  const onMarkRead = async (id) => {
    const r = await notificationsApi.markRead(id);
    setItems((rows) => rows.map((n) => (n.id === id ? { ...n, ...r.notification } : n)));
    setUnread(r.unread_count ?? 0);
  };

  const onMarkAllRead = async () => {
    const r = await notificationsApi.markAllRead();
    setUnread(r.unread_count ?? 0);
    // Re-fetch current tab to reflect read state
    refresh();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="notification-bell"
        aria-label="Notifications"
        aria-expanded={open ? "true" : "false"}
        aria-controls="notification-center"
        onClick={handleOpen}
        className="relative shrink-0 h-9 w-9 grid place-items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-blue)]"
        style={{ color: "var(--fg-primary)" }}
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-critical-red text-white text-[9.5px] font-semibold grid place-items-center tabular-nums"
          >
            {badgeText}
          </span>
        )}
      </button>
      {open && anchor && createPortal(
        <NotificationCenter
          anchor={anchor}
          items={items}
          tab={tab}
          onTab={setTab}
          unread={unread}
          loading={loading}
          onClose={() => setOpen(false)}
          onMarkRead={onMarkRead}
          onMarkAllRead={onMarkAllRead}
        />,
        document.body
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Notification Center — dropdown anchored to the bell.
// ---------------------------------------------------------------------------
const NotificationCenter = ({
  anchor,
  items,
  tab,
  onTab,
  unread,
  loading,
  onClose,
  onMarkRead,
  onMarkAllRead,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const filtered = items;

  return (
    <div
      id="notification-center"
      ref={ref}
      role="dialog"
      aria-label="Notification Center"
      data-testid="notification-center"
      style={{
        position: "fixed",
        top: anchor.top,
        right: anchor.right,
        left: anchor.narrow ? anchor.left : undefined,
        width: anchor.narrow ? "auto" : "min(380px, calc(100vw - 16px))",
        maxHeight: "min(70vh, 560px)",
        zIndex: 60,
        background: "var(--bg-surface)",
        color: "var(--fg-primary)",
        border: "1px solid var(--border-subtle)",
      }}
      className="rounded-md shadow-lg flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-steel">Notifications</div>
          <div className="text-[13px] font-medium">
            {unread > 0 ? `${unread} unread` : "You're all caught up."}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="notification-center-close"
          className="text-steel hover:text-carbon dark:hover:text-road-white p-1"
          aria-label="Close notifications"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex items-center justify-between px-3 pt-2 gap-2">
        <div role="tablist" className="flex items-center gap-1 text-[11.5px]">
          {["unread", "all"].map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              data-testid={`notification-tab-${t}`}
              onClick={() => onTab(t)}
              className={`px-2 py-1 rounded-sm ${tab === t
                ? "bg-coach-blue text-white"
                : "text-steel hover:text-carbon dark:hover:text-road-white"}`}
            >
              {t === "unread" ? `Unread${unread ? ` (${unread})` : ""}` : "All"}
            </button>
          ))}
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={onMarkAllRead}
            data-testid="notification-mark-all-read"
            className="inline-flex items-center gap-1 text-[11px] text-coach-blue hover:underline"
          >
            <CheckCheck size={12} /> Mark all read
          </button>
        )}
      </div>

      <div className="overflow-y-auto flex-1 mt-2">
        {loading && filtered.length === 0 && (
          <div data-testid="notification-loading" className="text-center text-[12px] text-steel py-6">Loading…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div data-testid="notification-empty" className="text-center py-10 px-4">
            <Inbox size={22} className="text-steel mx-auto mb-2" />
            <div className="text-[13px] font-medium">You're all caught up.</div>
            <div className="text-[11.5px] text-steel mt-1">No new notifications.</div>
          </div>
        )}
        <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
          {filtered.map((n) => (
            <NotificationRow
              key={n.id}
              n={n}
              onMarkRead={() => onMarkRead(n.id)}
              onClose={onClose}
            />
          ))}
        </ul>
      </div>
    </div>
  );
};

const NotificationRow = ({ n, onMarkRead, onClose }) => {
  const cat = CATEGORIES[n.category] || CATEGORIES.general;
  const pri = PRIORITIES[n.priority] || PRIORITIES.normal;
  const unread = !n.read_at;

  const inner = (
    <div className="flex items-start gap-2">
      <div className={`mt-1 w-1.5 h-1.5 rounded-full ${pri.dot}`}
           title={pri.label}
           data-testid={`notification-priority-${n.id}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-block text-[9.5px] uppercase tracking-widest rounded-sm px-1.5 py-0.5 ${cat.tint}`}>
            {cat.label}
          </span>
          {unread && (
            <span className="inline-flex items-center gap-1 text-[9.5px] uppercase text-critical-red">
              <Circle size={6} fill="currentColor" /> Unread
            </span>
          )}
          {n.condition_status === "active" && (
            <span
              className="inline-flex items-center text-[9.5px] uppercase tracking-widest rounded-sm px-1.5 py-0.5 bg-amber-hold/15 text-amber-hold border border-amber-hold/30"
              data-testid={`notification-condition-active-${n.id}`}
              title="Alert condition currently active"
            >
              Active
            </span>
          )}
          {n.condition_status === "resolved" && (
            <span
              className="inline-flex items-center text-[9.5px] uppercase tracking-widest rounded-sm px-1.5 py-0.5 bg-chrome-mist/40 text-steel border border-coach-silver/40"
              data-testid={`notification-condition-resolved-${n.id}`}
              title="Alert condition has cleared"
            >
              Resolved
            </span>
          )}
          <span className="text-[10.5px] text-steel ml-auto">{relativeTime(n.created_at)}</span>
        </div>
        <div className="text-[12.5px] mt-0.5 truncate" style={{ color: "var(--fg-primary)" }}>
          {n.title}
        </div>
        {n.message && (
          <div className="text-[11.5px] text-steel mt-0.5 line-clamp-2">
            {n.message}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <li
      data-testid={`notification-row-${n.id}`}
      data-unread={unread ? "true" : "false"}
      className={`group px-3 py-2 ${unread ? "bg-coach-blue/[0.03]" : ""}`}
    >
      {n.action_url ? (
        <Link
          to={n.action_url}
          data-testid={`notification-action-${n.id}`}
          onClick={() => { onMarkRead(); onClose(); }}
          className="block hover:bg-road-white dark:hover:bg-carbon/40 -mx-2 px-2 rounded-sm"
        >
          {inner}
        </Link>
      ) : (
        <div>{inner}</div>
      )}
      {unread && (
        <div className="flex justify-end mt-1.5">
          <button
            type="button"
            data-testid={`notification-mark-read-${n.id}`}
            onClick={onMarkRead}
            className="text-[11px] text-coach-blue hover:underline"
          >
            Mark read
          </button>
        </div>
      )}
    </li>
  );
};
